import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { stageByApiName } from '@/lib/domain/stages';
import { DEALS_QUERY_KEY, patchDealInCaches, restoreDealItem, snapshotDealItem } from './useDeals';
import { type ApiDealResponse, readApiETag } from './types';

interface MoveArgs {
  id: string;
  /** apiName da fase de destino (ex.: "DueDiligence"). */
  toStage: string;
  /** ETag (RowVersion base64) do item, para o If-Match. */
  etag: string;
  /**
   * Comentário da transição. OBRIGATÓRIO no retorno (mover para uma fase anterior) — o backend
   * rejeita retorno sem comentário (400). Opcional no avanço. O caller (kanban/stepper) coleta o
   * comentário no modal antes de chamar quando for retorno.
   */
  comment?: string;
}

/**
 * Move um deal de fase (drag-and-drop / stepper) via POST /api/deals/{id}/stage com If-Match (ETag).
 * Atualização otimista no cache; em 412 (alguém moveu antes) faz rollback + refetch + aviso.
 * O `stage` vai como NÚMERO (backend sem JsonStringEnumConverter → bind de enum por número).
 * Retorno (fase anterior) exige `comment`; avanço não.
 */
export function useMoveDealStage() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, toStage, etag, comment }: MoveArgs) => {
      const def = stageByApiName(toStage);
      if (!def) throw new Error(`Fase desconhecida: ${toStage}`);
      return apiFetch<ApiDealResponse>(`/api/deals/${id}/stage`, {
        method: 'POST',
        headers: { 'If-Match': `"${etag}"` },
        body: comment ? { stage: def.apiValue, comment } : { stage: def.apiValue },
      });
    },
    onMutate: async ({ id, toStage }) => {
      await qc.cancelQueries({ queryKey: DEALS_QUERY_KEY });
      const prev = snapshotDealItem(qc, id);
      patchDealInCaches(qc, id, (it) => ({
        ...it,
        currentStage: toStage,
        stageEnteredAtUtc: new Date().toISOString(),
      }));
      return { prev };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) restoreDealItem(qc, vars.id, ctx.prev);
      if ((err as ApiError).status === 412) {
        toast('Esse deal foi alterado por outra pessoa. Atualizei a lista.', 'warn');
        void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      } else {
        toast('Não foi possível mover o deal. Tente de novo.', 'danger');
      }
    },
    onSuccess: (resp) => {
      // Reconcilia com o servidor: ETag e data de entrada na fase novos (senão o próximo move dá 412).
      patchDealInCaches(qc, resp.id, (it) => ({
        ...it,
        currentStage: resp.currentStage,
        stageEnteredAtUtc: resp.stageEnteredAtUtc,
        eTag: readApiETag(resp) || it.eTag,
      }));
      toast('Fase atualizada.');
    },
  });
}
